import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Heading, Modal } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyDetailsWebApiHandler_overview } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import propertyUtils from '../../utils/propertyUtils';

import { useAuth } from '../../components/AuthProvider';
import SelectAvailableActions from '../modals/AddPropertyModal/components/SelectAvailableActions';

import ApplicationsModule from './modules/Applications/ApplicationsModule';
import InterestedRenters from './modules/Applications/InterestedRenters';
import UpcomingToursModule from './modules/Applications/UpcomingToursModule';
import InquiriesModule from './modules/Inquiries/InquiriesModule';
import LeasesModule from './modules/Leases/LeasesModule';
import ListingModule from './modules/Listing/ListingModule';
import NextStepsModule from './modules/NextSteps/NextStepsModule';
import PaymentsModule from './modules/Payments/PaymentsModule';
import TenantsModule from './modules/Tenants/TenantsModule';

type Props = {
  listingAlias: string;
};

const OverviewPage = ({ listingAlias }: Props) => {
  const { user } = useAuth();
  const router = useRouter();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  useEffect(() => {
    if (router.query.showSuccessModal === 'true') {
      setIsSuccessModalOpen(true);
      const { showSuccessModal: _, ...restQuery } = router.query;
      router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    }
  }, [router.query.showSuccessModal]);

  const { data: propertyOverviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const isActive = propertyOverviewData?.isActive ?? false;
  const isApplicationsRestricted = propertyOverviewData?.restrictions?.applications?.isAccessRestricted ?? false;
  const isLeasesRestricted = propertyOverviewData?.restrictions?.isLeasesRestricted ?? false;
  const isPaymentsRestricted = propertyOverviewData?.restrictions?.isPaymentsRestricted ?? false;
  const isEnterpriseCustomer = user?.isEnterpriseCustomer ?? false;
  const isLeaseBuilderRestricted = propertyOverviewData?.restrictions?.isLeaseBuilderRestricted ?? false;
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: propertyOverviewData });
  const street = propertyOverviewData?.address?.street ?? '';
  const propertyState = propertyOverviewData?.address?.state ?? '';
  const isFeed = false; // feed status comes from listing summary; default false for overview

  const renderActiveListingModules = () => (
    <>
      <ListingModule listingAlias={listingAlias} />
      <TenantsModule listingAlias={listingAlias} />
      {isEnterpriseCustomer ? (
        <InquiriesModule listingAlias={listingAlias} />
      ) : (
        !isRoomForRent && <InterestedRenters listingAlias={listingAlias} street={street} isFeed={isFeed} />
      )}
      {!isApplicationsRestricted && <ApplicationsModule listingAlias={listingAlias} street={street} />}
      {!isLeasesRestricted && (
        <LeasesModule
          listingAlias={listingAlias}
          isLeaseBuilderRestricted={isLeaseBuilderRestricted}
          propertyState={propertyState}
        />
      )}
      {!isPaymentsRestricted && <PaymentsModule listingAlias={listingAlias} />}
    </>
  );

  const renderInactiveListingModules = () => (
    <>
      <TenantsModule listingAlias={listingAlias} />
      {!isLeasesRestricted && (
        <LeasesModule
          listingAlias={listingAlias}
          isLeaseBuilderRestricted={isLeaseBuilderRestricted}
          propertyState={propertyState}
        />
      )}
      {!isPaymentsRestricted && <PaymentsModule listingAlias={listingAlias} />}
      <ListingModule listingAlias={listingAlias} />
    </>
  );

  const nextStepGridOffset = { base: '-16px', md: '-24px', xxl: 0 };

  return (
    <>
      {isSuccessModalOpen && (
        <Modal
          open={isSuccessModalOpen}
          onOpenChange={(open) => !open && setIsSuccessModalOpen(false)}
          scrollDocument={false}
          size={{ base: 'fullScreen', md: 'lg' }}
          header={<Heading level={2}>Zillow Rental Manager</Heading>}
          body={
            <SelectAvailableActions
              listingAlias={listingAlias}
              triggerObject="zrm_user_onboarding"
              user={user ?? undefined}
            />
          }
        />
      )}
      <Box
        data-testid="OverviewPage"
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(0,1fr))',
          gap: { xxl: '400' },
        }}
      >
        {/* Left column: primary modules */}
        <Box css={{ gridColumn: { base: '1/13', xxl: '1/8' }, display: 'flex', flexDirection: 'column', gap: '400' }}>
          {isActive ? renderActiveListingModules() : renderInactiveListingModules()}
        </Box>

        {/* Right column: UpcomingTours + NextSteps */}
        <Box
          css={{
            gridColumn: { base: '1/13', xxl: '8/13' },
            gridRowStart: { base: '1', xxl: 'inherit' },
            marginLeft: nextStepGridOffset,
            marginRight: nextStepGridOffset,
            marginBottom: { base: '400', xxl: 0 },
            display: 'flex',
            flexDirection: 'column',
            gap: '400',
          }}
        >
          <UpcomingToursModule listingAlias={listingAlias} isActive={isActive} />
          <NextStepsModule listingAlias={listingAlias} />
        </Box>
      </Box>
    </>
  );
};

export default OverviewPage;
